// src/resources/reports/ReportAppPenetration/SummaryCards.tsx
//
// Resumo da rede no topo — 6 cards, um por métrica, cada um já respondendo
// "quanto ficou dentro/fora do app" sem precisar abrir a tabela. Espelha o
// mockup aprovado (docs/superpowers/specs/2026-09-17-penetracao-app-detalhe-mockup.html).
import React from 'react';
import { Box, Typography } from '@mui/material';
import {
  MetricKey, METRIC_LABELS, METRIC_ORDER, RowScope, StationRow,
  SUMMABLE_METRICS, discountShareOfAppValue, formatMetricValue, fmtPct, metricSetFor, metricValueFor, shareFor,
} from './types';

const BLUE = '#3b82f6';
const GRAY = '#94a3b8';
const SUCCESS = '#16a34a';

const Card = ({ metricKey, row }: { metricKey: MetricKey; row: StationRow }) => {
  const total = metricSetFor(row, RowScope.Total);
  const app = metricSetFor(row, RowScope.App);
  const off = metricSetFor(row, RowScope.Off);
  const isSummable = SUMMABLE_METRICS.includes(metricKey);
  const isDiscounts = metricKey === MetricKey.Discounts;
  const isLiftMetric = metricKey === MetricKey.AvgTicket || metricKey === MetricKey.AvgVolume;

  const share = isSummable ? shareFor(row, metricKey) : isDiscounts ? discountShareOfAppValue(row) : null;
  const lift = metricKey === MetricKey.AvgTicket ? row.lift.avgTicket : metricKey === MetricKey.AvgVolume ? row.lift.avgVolume : null;

  return (
    <Box sx={{ bgcolor: '#fff', border: '1px solid #e2e8f0', borderRadius: 3, p: 1.5, minWidth: 0 }}>
      <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: GRAY }}>
        {METRIC_LABELS[metricKey]}
      </Typography>
      <Typography sx={{ fontSize: 19, fontWeight: 800, my: 0.5 }}>{formatMetricValue(metricKey, metricValueFor(total, metricKey))}</Typography>

      {isSummable && (
        <>
          <Box sx={{ height: 9, borderRadius: 999, bgcolor: '#e2e8f0', overflow: 'hidden' }}>
            <Box sx={{ height: '100%', width: `${Math.min(100, (share ?? 0) * 100)}%`, background: `linear-gradient(90deg, ${BLUE}, #06b6d4)` }} />
          </Box>
          <Typography sx={{ fontSize: 11, color: '#64748b', my: 0.5 }}>
            <b>{fmtPct(share, 1)}</b> no app
          </Typography>
        </>
      )}

      {isDiscounts && (
        <Typography sx={{ fontSize: 11, color: '#64748b', mb: 0.5 }}>{fmtPct(share, 1)} do valor no app</Typography>
      )}

      {isLiftMetric && lift !== null && (
        <Typography sx={{ fontSize: 11, fontWeight: 700, color: lift >= 0 ? SUCCESS : '#ef4444', mb: 0.5 }}>
          {lift >= 0 ? '▲' : '▼'} {fmtPct(Math.abs(lift), 1)} no app
        </Typography>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: 11.5, color: '#64748b' }}>
        <span style={{ color: BLUE, fontWeight: 700 }}>No app</span>
        <b>{formatMetricValue(metricKey, metricValueFor(app, metricKey))}</b>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: 11.5, color: '#64748b' }}>
        <span style={{ color: GRAY, fontWeight: 700 }}>Fora</span>
        <b>{isDiscounts ? '—' : formatMetricValue(metricKey, metricValueFor(off, metricKey))}</b>
      </Box>
    </Box>
  );
};

const SummaryCards = ({ totals }: { totals: StationRow }) => (
  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, 1fr)', lg: 'repeat(6, 1fr)' }, gap: 1.25, mb: 2 }}>
    {METRIC_ORDER.map(key => <Card key={key} metricKey={key} row={totals} />)}
  </Box>
);

export default SummaryCards;
